/** Shared nutrition capture types, used by both Procurement Products (supplier-provided
 * packaged-goods nutrition) and RecipeV2 (recipe-derived/computed nutrition).
 * UI-only for now - no backend persistence exists yet on either MarketItem or RecipeV2. */

/** Which side of the journey authored this nutrition record - nutrition data can come from
 * either direction (supplier catalogue data, or a chef-authored recipe). Reconciling which
 * source wins when both exist is a future/backend concern, not solved here. */
export enum NutritionSource {
  Recipe = 'recipe',
  Supplier = 'supplier',
}

export interface NutritionalValues {
  energyKj?: number;
  energyKcal?: number;
  fat?: number;
  saturates?: number;
  carbohydrate?: number;
  sugars?: number;
  fibre?: number;
  protein?: number;
  salt?: number;
}

export interface ProductNutritionalInfo {
  source?: NutritionSource;
  servingSize?: string;
  per100g: NutritionalValues;
  perServing: NutritionalValues;
}

export const emptyNutritionalInfo: ProductNutritionalInfo = {
  servingSize: '',
  per100g: {},
  perServing: {},
};

/** UK adult Reference Intakes (Regulation (EU) No 1169/2011, as retained in UK law) - used to
 * work out the "%RI per serving" figures shown on UK front-of-pack nutrition labels. */
export const UK_REFERENCE_INTAKE = {
  energyKj: 8400,
  energyKcal: 2000,
  fat: 70,
  saturates: 20,
  sugars: 90,
  salt: 6,
} as const;

export type TrafficLightLevel = 'low' | 'medium' | 'high';

/** FSA front-of-pack traffic light thresholds, evaluated per 100g of product. Only the four
 * HFSS-relevant nutrients (fat/saturates/sugars/salt) get a colour - energy/carbs/fibre/protein
 * aren't traffic-lighted under the FSA scheme. */
export const TRAFFIC_LIGHT_THRESHOLDS: Record<'fat' | 'saturates' | 'sugars' | 'salt', { low: number; high: number }> = {
  fat: { low: 3, high: 17.5 },
  saturates: { low: 1.5, high: 5 },
  sugars: { low: 5, high: 22.5 },
  salt: { low: 0.3, high: 1.5 },
};

export const getTrafficLightLevel = (
  nutrient: keyof typeof TRAFFIC_LIGHT_THRESHOLDS,
  per100gValue?: number,
): TrafficLightLevel | undefined => {
  if (per100gValue == null) return undefined;
  const { low, high } = TRAFFIC_LIGHT_THRESHOLDS[nutrient];
  if (per100gValue <= low) return 'low';
  if (per100gValue > high) return 'high';
  return 'medium';
};

/** Simplified HFSS (High in Fat, Sugar or Salt) indicator: flags a product/recipe as likely
 * HFSS when 2+ of the FSA traffic-light nutrients come out "high" per 100g, which is a common
 * proxy used ahead of running the full DHSC Nutrient Profiling Model (which also weighs energy,
 * fibre, protein and fruit/veg/nut content - not modelled here).
 * lazy: proxy only, not a certified NPM score - upgrade to the full NPM points calculation if
 * this needs to drive real HFSS restriction/placement decisions. */
export const isLikelyHfss = (per100g: NutritionalValues): boolean => {
  const highCount = (['fat', 'saturates', 'sugars', 'salt'] as const)
    .filter((nutrient) => getTrafficLightLevel(nutrient, per100g[nutrient]) === 'high')
    .length;
  return highCount >= 2;
};
